/**
 * OGP画像生成サービス
 * Custom-OGP.png をベースに、SNS名とコメントを合成したOGP画像を生成する
 */

const FONT_FAMILY = '"Noto Sans JP", sans-serif';
const TEXT_COLOR = 'rgb(233, 208, 202)';

/**
 * NotoSansJP フォントがロード済みか確認し、待機する
 */
async function ensureFontLoaded() {
  if (document.fonts) {
    try {
      // Google Fonts経由でindex.htmlからロード済みのフォントを待つ
      await document.fonts.load('bold 36px "Noto Sans JP"');
      await document.fonts.load('24px "Noto Sans JP"');
    } catch (e) {
      console.warn('[ogpGenerator] フォント待機失敗、フォールバック使用:', e);
    }
  }
}

/**
 * ドロップシャドウ付きでテキストを描画するヘルパー
 */
function fillTextWithShadow(ctx, text, x, y) {
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.12)';
  ctx.shadowBlur = 2;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 1;
  ctx.fillText(text, x, y);
  ctx.restore();
}

/**
 * テキストを指定幅で折り返して描画する
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} x - 描画領域の左上X
 * @param {number} y - 描画領域の左上Y
 * @param {number} maxWidth - 最大幅
 * @param {number} maxHeight - 最大高さ
 * @param {number} fontSize - 初期フォントサイズ
 * @returns {number} 実際に使用したフォントサイズ
 */
function drawWrappedText(ctx, text, x, y, maxWidth, maxHeight, fontSize) {
  if (!text) return fontSize;

  const lineHeight = fontSize * 1.75;
  const lines = [];

  // テキストを改行で分割し、各行をさらに幅で折り返す
  const paragraphs = text.split('\n');
  for (const paragraph of paragraphs) {
    if (paragraph === '') {
      lines.push('');
      continue;
    }
    let remaining = paragraph;
    while (remaining.length > 0) {
      let end = remaining.length;
      while (ctx.measureText(remaining.substring(0, end)).width > maxWidth && end > 1) {
        end--;
      }
      lines.push(remaining.substring(0, end));
      remaining = remaining.substring(end);
    }
  }

  // 全行が540px四方に収まるか確認、フォントサイズを調整
  const totalHeight = lines.length * lineHeight;
  if (totalHeight > maxHeight && fontSize > 14) {
    const newFontSize = Math.max(14, Math.floor(fontSize * (maxHeight / totalHeight)));
    ctx.font = `${newFontSize}px ${FONT_FAMILY}`;
    return drawWrappedText(ctx, text, x, y, maxWidth, maxHeight, newFontSize);
  }

  // テキストを中央揃えで描画（ドロップシャドウ付き）
  const startY = y + (maxHeight - totalHeight) / 2 + fontSize;
  for (let i = 0; i < lines.length; i++) {
    const lineWidth = ctx.measureText(lines[i]).width;
    const lineX = x + (maxWidth - lineWidth) / 2;
    fillTextWithShadow(ctx, lines[i], lineX, startY + i * lineHeight);
  }

  return fontSize;
}

/**
 * カスタムOGP画像を生成する
 * @param {string} snsUsername - SNSユーザー名（未設定時は「名無しの」を使用）
 * @param {string} comment - ユーザーコメント
 * @returns {Promise<string>} base64エンコードされたPNG画像データ
 */
export async function generateOgpImage(snsUsername, comment) {
  // フォントをロード
  await ensureFontLoaded();

  // ベース画像を読み込み
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = `${window.location.origin}/Custom-OGP.png`;

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = () => reject(new Error('OGPベース画像のロードに失敗しました'));
  });

  // Canvas作成 (1200x630)
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 630;
  const ctx = canvas.getContext('2d');

  // ベース画像を描画
  ctx.drawImage(img, 0, 0, 1200, 630);

  // テキスト色を設定
  ctx.fillStyle = TEXT_COLOR;
  ctx.textBaseline = 'top';

  // 「XXXXのブクマす」を描画（ロゴバーの直下）
  const displayName = snsUsername || '名無しの';
  const titleText = `${displayName}のブクマす`;
  ctx.font = `bold 36px ${FONT_FAMILY}`;
  const titleWidth = ctx.measureText(titleText).width;
  fillTextWithShadow(ctx, titleText, (1200 - titleWidth) / 2, 112);

  // コメントを540px四方に収まるように描画
  if (comment) {
    const commentBoxX = (1200 - 540) / 2; // 中央配置
    const commentBoxY = 117;
    const commentMaxHeight = 390; // 195〜585の範囲

    ctx.font = `24px ${FONT_FAMILY}`;
    drawWrappedText(ctx, comment, commentBoxX, commentBoxY, 540, commentMaxHeight, 24);
  }

  // PNG として書き出し
  return canvas.toDataURL('image/png');
}
